/** File explorer tree with create/refresh actions. */

import { useMemo, useState } from "react";
import type { FileNode } from "../types";
import { FilesService } from "../services";

interface Props {
  tree: FileNode;
  activePath: string | null;
  onOpen: (path: string) => void;
  onRefresh: () => void;
  onCollapse?: () => void;
}

/** Return True if the node is a .tex file or a directory containing one. */
function isTexFile(name: string): boolean {
  return name.toLowerCase().endsWith(".tex");
}

/**
 * Prune the tree so only `.tex` files remain, along with directories that
 * (recursively) contain at least one `.tex` file. Build artifacts such as
 * `.aux`, `.log`, `.pdf`, `.fls`, and the `_minted/` cache are hidden.
 */
function filterTexTree(node: FileNode): FileNode | null {
  if (node.type === "file") {
    return isTexFile(node.name) ? node : null;
  }
  const kept: FileNode[] = [];
  for (const child of node.children) {
    const pruned = filterTexTree(child);
    if (pruned) kept.push(pruned);
  }
  if (kept.length === 0) return null;
  return { ...node, children: kept };
}

function isEditable(name: string): boolean {
  return isTexFile(name);
}

function TreeView({
  node,
  depth,
  activePath,
  onOpen,
}: {
  node: FileNode;
  depth: number;
  activePath: string | null;
  onOpen: (path: string) => void;
}): JSX.Element {
  const [expanded, setExpanded] = useState(depth < 1);
  if (node.type === "file") {
    const editable = isEditable(node.name);
    return (
      <div
        className={`tree-row${activePath === node.path ? " active" : ""}`}
        style={{ paddingLeft: 8 + depth * 12 }}
        onClick={() => editable && onOpen(node.path)}
        title={node.path}
      >
        <span className="icon">{editable ? "📄" : "🔒"}</span>
        <span>{node.name}</span>
      </div>
    );
  }
  return (
    <div>
      <div
        className="tree-row dir"
        style={{ paddingLeft: 8 + depth * 12 }}
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="icon">{expanded ? "▾" : "▸"}</span>
        <span className="icon">📁</span>
        <span>{node.name}</span>
      </div>
      {expanded &&
        node.children.map((child) => (
          <TreeView
            key={child.path}
            node={child}
            depth={depth + 1}
            activePath={activePath}
            onOpen={onOpen}
          />
        ))}
    </div>
  );
}

export function FileExplorer({
  tree,
  activePath,
  onOpen,
  onRefresh,
  onCollapse,
}: Props): JSX.Element {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  // Only show .tex files (and directories that contain them).
  // If no .tex files are found, show an empty tree rather than the
  // full tree (which would expose non-.tex files).
  const visibleTree = useMemo(() => {
    const filtered = filterTexTree(tree);
    return filtered ?? { ...tree, children: [] };
  }, [tree]);

  const createFile = async (): Promise<void> => {
    if (!newName.trim()) {
      setCreating(false);
      return;
    }
    try {
      await FilesService.create(newName.trim());
      setNewName("");
      setCreating(false);
      onRefresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Create failed");
    }
  };

  return (
    <div className="explorer">
      <div className="panel-title">
        <span>Files</span>
        <span>
          <button
            title="New file"
            onClick={() => setCreating(true)}
            style={{ padding: "0 6px" }}
          >
            +
          </button>
          <button
            title="Refresh"
            onClick={onRefresh}
            style={{ padding: "0 6px", marginLeft: 4 }}
          >
            ↻
          </button>
          {onCollapse && (
            <button
              title="Collapse file explorer"
              onClick={onCollapse}
              style={{ padding: "0 6px", marginLeft: 4 }}
            >
              ◀
            </button>
          )}
        </span>
      </div>
      {creating && (
        <div style={{ padding: "6px 8px" }}>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="path/to/file.tex"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") createFile();
              if (e.key === "Escape") setCreating(false);
            }}
            onBlur={createFile}
            style={{ width: "100%" }}
          />
        </div>
      )}
      <div className="tree">
        {visibleTree.children.length === 0 && (
          <div className="empty">No .tex files</div>
        )}
        {visibleTree.children.map((child) => (
          <TreeView
            key={child.path}
            node={child}
            depth={0}
            activePath={activePath}
            onOpen={onOpen}
          />
        ))}
      </div>
    </div>
  );
}
